/* eslint-disable react/react-in-jsx-scope -- Unaware of jsxImportSource */
/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useState, ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { TableContainer as CustomTableContainer } from "../components/TableContainer";
import { useAppSelector, useAppDispatch } from "../../../../states/hooks";
import { actions, selectIsDarkMode } from "../../../../states/appState";
import { selectMiners } from "../../../../states/minersState";
import { selectPoolConfig } from "../../../../states/poolConfigState";
import { formatTime } from "../../../utils/functions/formatTime";
import { formatCapacity } from "../../../utils/functions/formatCapacity";
import { asRSAddress } from "../../../utils/functions/accountAddress";
import { columns } from "./columns";

import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import Tooltip from "@mui/material/Tooltip";
import TableRow from "@mui/material/TableRow";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import BlurOnIcon from "@mui/icons-material/BlurOn";
import StorageIcon from "@mui/icons-material/Storage";
import TimerIcon from "@mui/icons-material/Timer";
import VerifiedIcon from "@mui/icons-material/Verified";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PaidIcon from "@mui/icons-material/Paid";
import HubIcon from "@mui/icons-material/Hub";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

type showAllMiners = "yes" | "no";
const showAllMinersKey = "showAllMiners";

interface MinersListProps {
    showTopMiners?: boolean;
}

export const MinersList = ({ showTopMiners = false }: MinersListProps) => {
    const { t } = useTranslation();
    const { blocksForAverage, processLag } = useAppSelector(selectPoolConfig);
    const dispatch = useAppDispatch();
    const isDarkMode = useAppSelector(selectIsDarkMode);
    const theme = useTheme();
    const isCardLayout = true;
    const miningData = useAppSelector(selectMiners);
    const {
        isLoading,
        miners,
        totalPhysicalCapacity,
        totalEffectiveCapacity,
        totalSharedCapacity,
    } = miningData;
    const { setSearchMiner } = actions;

    const minersPerPageMobile = 12;
    const minersPerPageDesktop = 24;
    const maxSubmissions = blocksForAverage + processLag;
    const defaultValue = localStorage.getItem(showAllMinersKey) || "no";

    const [canSeeAllMiners, setCanSeeAllMiners] =
        // @ts-ignore
        useState<showAllMiners>(defaultValue);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(
        isCardLayout ? minersPerPageMobile : minersPerPageDesktop
    );

    const handleChangePage = (event: unknown, newPage: number) => setPage(newPage);

    const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    const switchSeeAllMinersStatus = () => {
        const finalOption = canShowAllMiners ? "no" : "yes";
        setCanSeeAllMiners(finalOption);
        localStorage.setItem(showAllMinersKey, finalOption);
    };

    const canShowAllMiners = canSeeAllMiners === "yes";
    const rows = showTopMiners ? miners.slice(0, 10) : miners;

    const tableMaxHeight =
        canShowAllMiners || isCardLayout || rows.length < minersPerPageDesktop
            ? "auto"
            : 680;

    if (isLoading) return <Skeleton width="100%" height={140} animation="wave" />;

    if (!rows.length)
        return (
            <Grid container mx="auto" maxWidth={760} mt={1} p={2} direction="row" alignItems="center">
                <CustomTableContainer>
                    <Typography align="center" variant="h6" color="textSecondary">
                        {t("noMiners")}
                    </Typography>
                </CustomTableContainer>
            </Grid>
        );

    const paginatedRows = rows.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    const openMiner = (accountId: string) => dispatch(setSearchMiner(accountId));

    const currentDeadlineLabel = (miner: any) => {
        const value = formatTime(miner.currentRoundBestDeadline);
        return value === "waiting..." ? t("waiting") + "..." : value;
    };

    const CardValue = ({ label, value, icon, help }: { label: string; value: any; icon?: any; help?: string }) => (
        <Tooltip title={help || label} arrow placement="top">
            <div className="smMinerCardStat">
                <span>{icon}{label}</span>
                <strong>{value || "-"}</strong>
            </div>
        </Tooltip>
    );

    if (isCardLayout) {
        return (
            <Paper elevation={0} className="smMinerCardsPanel smMinerCardsPanelDesktop">
                <div className="smCardsInfoToolbar">
                    <div>
                        <span><InfoOutlinedIcon /> {showTopMiners ? t("top10Miners") : t("minersList")}</span>
                        <strong>{showTopMiners ? "Die stärksten Miner kompakt als Karten." : "Miner als übersichtliche Premium-Cards statt klassischer Tabelle."}</strong>
                    </div>
                    <div className="smCardsLegend">
                        <Chip icon={<TimerIcon />} label="Deadline" size="small" />
                        <Chip icon={<StorageIcon />} label="Kapazität" size="small" />
                        <Chip icon={<VerifiedIcon />} label="Bestätigt" size="small" />
                    </div>
                </div>
                {!showTopMiners && (
                    <div className="smPoolTotalCard">
                        <div>
                            <span>{t("poolTotal")}</span>
                            <strong>{formatCapacity(totalPhysicalCapacity as any)}</strong>
                        </div>
                        <div>
                            <span>{t("effectiveCapacity")}</span>
                            <strong>{formatCapacity(totalEffectiveCapacity as any)}</strong>
                        </div>
                        <div>
                            <span>{t("sharedCapacity")}</span>
                            <strong>{formatCapacity(totalSharedCapacity as any)}</strong>
                        </div>
                    </div>
                )}

                <Grid container spacing={2}>
                    {paginatedRows.map((miner: any, index: number) => {
                        const minerLabel = miner.name || asRSAddress(miner.accountId);
                        const deadlineLabel = currentDeadlineLabel(miner);
                        return (
                            <Grid item xs={12} sm={6} md={4} xl={3} key={miner.accountId || index}>
                                <Paper className="smMinerDataCard" elevation={0}>
                                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                                        <div className="smMinerCardIdentity">
                                            <Chip icon={<BlurOnIcon />} label={index + 1 + page * rowsPerPage} size="small" className="smRankChip" />
                                            <Typography component="button" onClick={() => openMiner(miner.accountId)} className="smMinerCardName">
                                                {minerLabel}
                                            </Typography>
                                        </div>
                                        <Chip size="small" icon={<VerifiedIcon />} label={`${miner.confirmedDeadlines}/${maxSubmissions}`} className="smConfirmedChip" />
                                    </Stack>

                                    <Grid container spacing={1.2} className="smMinerCardStatsGrid">
                                        <Grid item xs={6}><CardValue icon={<TimerIcon />} help="Aktuelle Deadline dieses Miners in der laufenden Runde." label="Deadline" value={deadlineLabel} /></Grid>
                                        <Grid item xs={6}><CardValue icon={<PaidIcon />} help="Noch nicht ausgezahltes SIGNA-Guthaben des Miners." label="Guthaben" value={miner.pendingBalance} /></Grid>
                                        <Grid item xs={6}><CardValue icon={<StorageIcon />} help="Reale gemeldete Plot-Kapazität." label="Physisch" value={formatCapacity(miner.physicalCapacity)} /></Grid>
                                        <Grid item xs={6}><CardValue icon={<TrendingUpIcon />} help="Kapazität nach PoC+/Commitment-Bewertung." label="Effektiv" value={formatCapacity(miner.effectiveCapacity)} /></Grid>
                                        <Grid item xs={6}><CardValue icon={<HubIcon />} help="Für die Reward-Verteilung relevante geteilte Kapazität." label="Geteilt" value={formatCapacity(miner.sharedCapacity)} /></Grid>
                                        <Grid item xs={6}><CardValue icon={<InfoOutlinedIcon />} help="Reward-Modell / Anteil nach Pool-Regeln." label="Modell" value={`${miner.shareModel || 0}%`} /></Grid>
                                    </Grid>

                                    <Button fullWidth className="smCardDetailsButton" onClick={() => openMiner(miner.accountId)}>
                                        {t("viewMinerDetails")}
                                    </Button>
                                </Paper>
                            </Grid>
                        );
                    })}
                </Grid>

                <Grid container justifyContent="center" className="smPaginationWrap">
                    <TablePagination
                        component="div"
                        rowsPerPageOptions={[minersPerPageMobile]}
                        count={rows.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Grid>
            </Paper>
        );
    }

    return (
        <Paper elevation={0} className="smPremiumTablePanel">
            <TableContainer sx={{ maxHeight: tableMaxHeight }} className="smPremiumTableScroll">
                <Table stickyHeader aria-label="miners table" className="smPremiumTable">
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.id}
                                    align={column.align}
                                    sx={{ minWidth: column.minWidth, borderRight: 1, borderColor: "divider" }}
                                    css={css`:last-child { border-right: 0 !important; }`}
                                >
                                    {t(column.id)}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {!showTopMiners && (
                            <TableRow className="smPoolTotalRow">
                                {columns.map((column) => {
                                    let cellContent: any = "-";
                                    switch (column.id) {
                                        case "miner": cellContent = t("poolTotal"); break;
                                        case "physicalCapacity": cellContent = formatCapacity(totalPhysicalCapacity as any); break;
                                        case "effectiveCapacity": cellContent = formatCapacity(totalEffectiveCapacity as any); break;
                                        case "sharedCapacity": cellContent = formatCapacity(totalSharedCapacity as any); break;
                                        default: break;
                                    }

                                    return (
                                        <TableCell
                                            key={column.id}
                                            align={column.align}
                                            sx={{ fontWeight: 900, minWidth: column.minWidth, borderRight: 1, borderColor: "divider", backgroundColor: isDarkMode ? "rgba(55,244,200,0.10)" : "rgba(0,210,255,0.08)" }}
                                            css={css`:last-child { border-right: 0 !important; }`}
                                        >
                                            {cellContent}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        )}

                        {paginatedRows.map((miner: any, index: number) => (
                            <TableRow hover tabIndex={-1} key={miner.accountId || index} className="smPremiumTableRow">
                                {columns.map((column) => {
                                    const value = miner[column.id];
                                    let cellContent: any = null;
                                    switch (column.id) {
                                        case "miner":
                                            cellContent = (
                                                <Tooltip placement="right" title={t("viewMinerDetails") || ""} arrow>
                                                    <Typography className="smMinerLink" onClick={() => openMiner(miner.accountId)}>
                                                        {miner.name || asRSAddress(miner.accountId)}
                                                    </Typography>
                                                </Tooltip>
                                            );
                                            break;
                                        case "currentDeadline":
                                            const data = formatTime(miner.currentRoundBestDeadline);
                                            cellContent = data === "waiting..." ? <CircularProgress size={22} /> : data;
                                            break;
                                        case "confirmedDeadline": cellContent = miner.confirmedDeadlines + "/" + maxSubmissions; break;
                                        case "physicalCapacity": cellContent = formatCapacity(miner.physicalCapacity); break;
                                        case "effectiveCapacity": cellContent = formatCapacity(miner.effectiveCapacity); break;
                                        case "sharedCapacity": cellContent = formatCapacity(miner.sharedCapacity); break;
                                        case "shareModel": cellContent = value + "%"; break;
                                        case "committedBalance": cellContent = miner.totalCommitment; break;
                                        case "pocBoost": cellContent = miner.pocBostPool ? miner.pocBostPool.toFixed(3) : 0; break;
                                        default: cellContent = value; break;
                                    }

                                    return (
                                        <TableCell
                                            key={column.id}
                                            align={column.align}
                                            sx={{ borderRight: 1, borderColor: "divider", fontWeight: 800, fontSize: 14 }}
                                            css={css`:last-child { border-right: 0 !important; }`}
                                        >
                                            {cellContent}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Grid container direction="row" justifyContent="flex-end" alignItems="center" className="smPaginationWrap">
                {rows.length > minersPerPageDesktop && (
                    <Grid item>
                        <Button variant="outlined" className="smShowAllButton" onClick={switchSeeAllMinersStatus}>
                            {!canShowAllMiners ? t("showAllMiners") : t("stopShowAllMiners")}
                        </Button>
                    </Grid>
                )}

                <Grid item>
                    <TablePagination
                        component="div"
                        rowsPerPageOptions={[0]}
                        count={rows.length}
                        rowsPerPage={canShowAllMiners ? 500 : rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Grid>
            </Grid>
        </Paper>
    );
};
